/** "Suggested study topics" warm-up: when the learner comes back to Jungle (any page), the Worker brings a few recently
 * active classes' suggestions up to date in the background (POST /tutor/classes/study-suggestions/warm; it picks up to
 * three classes, a close exam first, and calls the model only when their topics materially changed). A class opened
 * next then shows a current card at once. Sent at most once per 20 minutes per account in this browser, on app load
 * and when a tab comes back after 30 minutes away. Never blocks or fails anything: a failed send is dropped. */
import { useEffect } from "react";
import { getFirebaseAuthorizationHeaders } from "../../../backend/utils/firebase-auth-headers";
import { JUNGLE_TUTOR_API_BASE_URL } from "../../../constants/GlobalTypesAndEnumsAndConstants";
import { getSignedInFirebaseUser } from "../../../backend/utils/anonymous-read-session";

const STORAGE_PREFIX = "jungle.tutorStudyWarm.v1:";
export const TUTOR_STUDY_WARM_INTERVAL_MS = 20 * 60_000;
const AWAY_MS = 30 * 60_000;

export interface TutorStudyWarmupDependencies {
  uid: string;
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  now(): number;
  send(): Promise<unknown>;
}
/** Sends the warm-up unless this account sent one in the last 20 minutes. Returns whether it was sent. */
export function warmTutorStudySuggestionsIfDue(dependencies: TutorStudyWarmupDependencies): boolean {
  const key = STORAGE_PREFIX + dependencies.uid;
  let last = NaN;
  try { last = Number(dependencies.storage?.getItem(key) ?? NaN); } catch { last = NaN; }
  const now = dependencies.now();
  if (Number.isFinite(last) && now - last >= 0 && now - last < TUTOR_STUDY_WARM_INTERVAL_MS) return false;
  try { dependencies.storage?.setItem(key, String(now)); } catch { /* private mode: no throttle memory */ }
  void dependencies.send().catch(() => undefined);
  return true;
}

async function sendTutorStudyWarmup(): Promise<void> {
  const headers = await getFirebaseAuthorizationHeaders();
  let timeZone = "UTC";
  try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { timeZone = "UTC"; }
  await fetch(`${JUNGLE_TUTOR_API_BASE_URL}/tutor/classes/study-suggestions/warm`, { method: "POST", cache: "no-store",
    headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify({ timeZone }), signal: AbortSignal.timeout(10_000) });
}

/** App-wide: once the Tutor is available to this learner, warm on load and on a return after a long time away. */
export function useTutorStudySuggestionsWarmup(tutorAvailable: boolean): void {
  useEffect(() => {
    if (!tutorAvailable) return;
    const warm = () => {
      const uid = getSignedInFirebaseUser()?.uid;
      if (uid) warmTutorStudySuggestionsIfDue({ uid, storage: window.localStorage, now: () => Date.now(), send: sendTutorStudyWarmup });
    };
    warm();
    let hiddenAt: number | null = null;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") { hiddenAt = Date.now(); return; }
      if (hiddenAt !== null && Date.now() - hiddenAt >= AWAY_MS) warm();
      hiddenAt = null;
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [tutorAvailable]);
}
